// src/components/schedules/SchedulesHistory.tsx
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Mail, Calendar, Clock, FileText, Search, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, TrendingUp, Users, Database, Zap, Link as LinkIcon, AlertTriangle
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
const TZ = "Africa/Casablanca";

function cleanBearer(t?: string | null) {
  let raw = (t || "").trim();
  if (raw.toLowerCase().startsWith("bearer ")) raw = raw.slice(7).trim();
  return raw;
}

type HistoryItem = {
  id: number;
  report_id: number;
  report_name: string;
  schedule_id: number | null;
  schedule_label: string | null;
  recipient_count: number;
  recipients: string;
  sent_at: string;
  row_count: number;
  csv_size_bytes: number;
  delivery_mode: string;
  download_token: string | null;
  success: number;
  error_message: string | null;
  execution_time_ms: number;
  triggered_by: string;
};

type Stats = {
  total_deliveries: number;
  successful_deliveries: number;
  failed_deliveries: number;
  success_rate: number;
  total_recipients: number;
  total_rows: number;
  total_size_mb: number;
  avg_execution_time_ms: number;
  by_delivery_mode: Record<string, number>;
  by_report: Array<{
    report_id: number;
    report_name: string;
    delivery_count: number;
    success_count: number;
    total_recipients: number;
  }>;
  by_day: Array<{
    date: string;
    count: number;
    success_count: number;
  }>;
  last_7_days: number;
};

async function apiGetHistory(days: number = 30): Promise<HistoryItem[]> {
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const res = await fetch(`${API_BASE}/report-delivery/history?days=${days}`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error("Impossible de charger l'historique");
  return res.json();
}

async function apiGetStats(days: number = 30): Promise<Stats> {
  const stored = localStorage.getItem("auth_token");
  const token = cleanBearer(stored);
  const res = await fetch(`${API_BASE}/report-delivery/history/stats?days=${days}`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error("Impossible de charger les statistiques");
  return res.json();
}

function parseSentAt(s?: string | null): Date | null {
  if (!s) return null;
  let raw = s.trim();
  if (!raw) return null;
  let norm = raw.includes("T") ? raw : raw.replace(" ", "T");
  const hasTZ = /[zZ]$|[+\-]\d{2}:\d{2}$/.test(norm);
  if (!hasTZ) norm += "Z";
  const d = new Date(norm);
  return isNaN(d.getTime()) ? null : d;
}

function fmtDate(d: Date | null): string {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      timeZone: TZ,
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return d.toLocaleDateString("fr-FR");
  }
}

function fmtTime(d: Date | null): string {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      timeZone: TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d);
  } catch {
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
}

export function SchedulesHistory() {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [daysFilter, setDaysFilter] = useState(30);

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [historyData, statsData] = await Promise.all([
          apiGetHistory(daysFilter),
          apiGetStats(daysFilter)
        ]);
        if (mounted) {
          setItems(Array.isArray(historyData) ? historyData : []);
          setStats(statsData);
        }
        setPage(1);
      } catch (e: any) {
        if (mounted) setErr(e?.message || "Erreur inconnue");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [daysFilter]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((it) => {
      const inName = it.report_name.toLowerCase().includes(term);
      const inRecipients = (it.recipients || "").toLowerCase().includes(term);
      const inId = String(it.report_id).includes(term);
      const inLabel = (it.schedule_label || "").toLowerCase().includes(term);
      return inName || inRecipients || inId || inLabel;
    });
  }, [items, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [q]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{err}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sélecteur de période */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">Période :</span>
        {[7, 30, 90].map((d) => (
          <Button
            key={d}
            variant={daysFilter === d ? "default" : "outline"}
            size="sm"
            onClick={() => setDaysFilter(d)}
          >
            {d} jours
          </Button>
        ))}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Envois totaux</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_deliveries}</div>
              <p className="text-xs text-muted-foreground">
                {stats.last_7_days} derniers 7 jours
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taux de réussite</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.success_rate}%</div>
              <p className="text-xs text-muted-foreground">
                {stats.successful_deliveries} / {stats.total_deliveries}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Destinataires</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_recipients}</div>
              <p className="text-xs text-muted-foreground">Emails envoyés</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Volume total</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_size_mb.toFixed(1)} Mo</div>
              <p className="text-xs text-muted-foreground">
                {stats.total_rows.toLocaleString()} lignes
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats supplémentaires */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Top Rapports */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Top Rapports
              </CardTitle>
              <CardDescription>Rapports les plus envoyés</CardDescription>
            </CardHeader>
            <CardContent>
              {stats.by_report.length === 0 ? (
                <Alert>
                  <AlertDescription>Aucune donnée disponible</AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  {stats.by_report.slice(0, 5).map((r) => (
                    <div key={r.report_id} className="flex items-center justify-between p-3 rounded-lg border">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{r.report_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {r.delivery_count} envois • {r.total_recipients} destinataires
                        </div>
                      </div>
                      <Badge variant={r.success_count === r.delivery_count ? "default" : "secondary"}>
                        {r.success_count}/{r.delivery_count}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modes de livraison */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Modes de Livraison
              </CardTitle>
              <CardDescription>Répartition par type d'envoi</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.by_delivery_mode).length === 0 ? (
                <Alert>
                  <AlertDescription>Aucune donnée disponible</AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  {Object.entries(stats.by_delivery_mode).map(([mode, count]) => (
                    <div key={mode} className="flex items-center justify-between p-3 rounded-lg border">
                      <div className="flex items-center gap-2">
                        {mode === "attachment" && <Mail className="h-4 w-4 text-muted-foreground" />}
                        {mode === "link" && <LinkIcon className="h-4 w-4 text-muted-foreground" />}
                        <span className="font-medium">
                          {mode === "attachment" ? "Pièce jointe" : "Lien téléchargement"}
                        </span>
                      </div>
                      <Badge>{count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Liste des envois */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Historique des envois</CardTitle>
              <CardDescription>Détails de chaque envoi de rapport</CardDescription>
            </div>
            <div className="flex items-center gap-2 w-full md:w-[320px]">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground pt-4">
              <div>
                {filtered.length} élément{filtered.length > 1 ? "s" : ""} • page {currentPage} / {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Suivant
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent>
          {pageItems.length === 0 ? (
            <Alert>
              <AlertDescription>
                {q.trim() ? "Aucun résultat trouvé" : "Aucun envoi enregistré"}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {pageItems.map((it) => {
                const d = parseSentAt(it.sent_at);
                const isSuccess = it.success === 1;
                return (
                  <div key={it.id} className="p-4 rounded-lg border hover:bg-accent/50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold">{it.report_name}</h3>
                          <Badge variant={isSuccess ? "default" : "destructive"} className="flex items-center gap-1">
                            {isSuccess ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                            {isSuccess ? "Envoyé" : "Échec"}
                          </Badge>
                          {it.schedule_label && (
                            <Badge variant="outline">
                              <Clock className="h-3 w-3 mr-1" />
                              {it.schedule_label}
                            </Badge>
                          )}
                          {it.delivery_mode === "link" && (
                            <Badge variant="secondary">
                              <LinkIcon className="h-3 w-3 mr-1" />
                              Lien
                            </Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            <span>{fmtDate(d)}</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            <span>{fmtTime(d)}</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <Users className="h-3 w-3" />
                            <span>{it.recipient_count} destinataire{it.recipient_count > 1 ? "s" : ""}</span>
                          </div>

                          <div className="flex items-center gap-2">
                            <Database className="h-3 w-3" />
                            <span>{it.row_count.toLocaleString()} lignes • {fmtSize(it.csv_size_bytes)}</span>
                          </div>
                        </div>

                        {it.recipients && (
                          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {it.recipients}
                          </div>
                        )}

                        {!isSuccess && it.error_message && (
                          <Alert variant="destructive" className="mt-2">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>{it.error_message}</AlertDescription>
                          </Alert>
                        )}
                      </div>

                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Zap className="h-3 w-3" />
                        {it.execution_time_ms}ms
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filtered.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <div className="text-sm text-muted-foreground">
                {filtered.length} élément{filtered.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Suivant
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

